/**
 * Tab: Histórico de events — read-only com filtros + active alerts.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, AlertTriangle, RefreshCcw } from "lucide-react";
import toast from "react-hot-toast";

import { alertEventsApi } from "@/api/alerts";
import { alvoDoEvento } from "@/lib/eventoAlvo";
import { apiErrorMessage } from "@/api/client";
import {
  alertQk,
  useActiveAlerts,
  useAlertEvents,
  useEquipments,
} from "@/hooks/queries";
import type { ActiveAlert, AlertEvent, Severity } from "@/types";

import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Field, Select } from "@/components/Field";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { formatDate, timeAgo } from "@/lib/format";
import { SEVERIDADE_TOM } from "@/lib/severidade";
import { SEVERIDADE_OPCOES } from "@/lib/severidade";

// Mapa compartilhado (lib/severidade.ts): este arquivo tinha a própria
// cópia, e eram dez ao todo. Ver o cabeçalho de lá.
const SEVERITY_TONE = SEVERIDADE_TOM;

const EVENT_TYPE_LABEL: Record<string, string> = {
  fired:                "Disparou",
  recovered:            "Recuperou",
  acknowledged:         "Reconhecido",
  renotified:           "Lembrete",
  auto_closed:          "Fechado auto",
  manually_closed:      "Fechado manual",
  notification_sent:    "Notificado",
  notification_failed:  "Falha de notificação",
};

const EVENT_TYPE_TONE: Record<string, "danger" | "success" | "warning" | "default"> = {
  fired:               "danger",
  recovered:           "success",
  acknowledged:        "default",
  renotified:          "warning",
  auto_closed:         "default",
  manually_closed:     "default",
  notification_sent:   "default",
  notification_failed: "warning",
};

export function EventsTab({ tenantId }: { tenantId?: string }) {
  const equipments = useEquipments();
  const active = useActiveAlerts(tenantId);
  const qc = useQueryClient();

  const [equipmentFilter, setEquipmentFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const events = useAlertEvents({
    equipment_id: equipmentFilter || undefined,
    severity: severityFilter || undefined,
    event_type: typeFilter || undefined,
    tenant_id: tenantId,
  });

  const equipmentNameById = new Map(
    (equipments.data ?? []).map((e) => [e.id, e.name]),
  );

  const ackMut = useMutation({
    mutationFn: ({ ruleId, equipmentId }: { ruleId: string; equipmentId: string }) =>
      alertEventsApi.acknowledge(ruleId, equipmentId),
    onSuccess: () => {
      toast.success("Alerta reconhecido");
      // Invalida prefix-only pra cobrir variações de tenantId/filtros.
      void qc.invalidateQueries({ queryKey: ["active-alerts"] });
      void qc.invalidateQueries({ queryKey: ["alert-events"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<AlertEvent>[] = [
    {
      key: "created_at",
      header: "Quando",
      cell: (r) => (
        <div>
          <div className="text-sm">{timeAgo(r.created_at)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{formatDate(r.created_at)}</div>
        </div>
      ),
    },
    {
      key: "event_type",
      header: "Evento",
      cell: (r) => (
        <Badge tone={EVENT_TYPE_TONE[r.event_type] ?? "default"}>
          {EVENT_TYPE_LABEL[r.event_type] ?? r.event_type}
        </Badge>
      ),
    },
    {
      key: "severity",
      header: "Sev",
      cell: (r) =>
        r.severity ? (
          <Badge tone={SEVERITY_TONE[r.severity as Severity]}>{r.severity}</Badge>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
        ),
    },
    {
      key: "equipment",
      header: "Ativo",
      // Evento de site (Camada 2) não tem equipment_id — mostrava "—" e a
      // linha mais grave da tabela era a que menos dizia de onde veio.
      cell: (r) => {
        const alvo = alvoDoEvento(r, equipmentNameById);
        if (alvo.escopo === "site") {
          return (
            <span className="inline-flex items-center gap-1.5">
              <Badge tone="neutral">Site</Badge>
              <span className="text-slate-700 dark:text-slate-300">
                {alvo.nome ?? "site não identificado"}
              </span>
            </span>
          );
        }
        if (alvo.escopo === "equipamento") {
          return (
            <span className="text-slate-700 dark:text-slate-300">
              {alvo.nome ?? r.equipment_id!.slice(0, 8)}
            </span>
          );
        }
        return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>;
      },
      hideOnMobile: true,
    },
    {
      key: "message",
      header: "Mensagem",
      cell: (r) => <span className="text-sm text-slate-700 dark:text-slate-300">{r.message ?? "—"}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Active */}
      {active.data && active.data.length > 0 && (
        <Card className="border-red-200">
          <CardHeader
            title={
              <span className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Alertas ativos ({active.data.length})
              </span>
            }
            subtitle="Estado atual dos firing — ack pra silenciar até recuperar."
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-surface-100 dark:divide-slate-800">
              {active.data.map((a) => (
                <ActiveAlertRow
                  key={`${a.rule_id}-${a.equipment_id}`}
                  alert={a}
                  onAck={() => ackMut.mutate({ ruleId: a.rule_id, equipmentId: a.equipment_id })}
                  loading={ackMut.isPending}
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* Filtros */}
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Ativo">
          <Select
            value={equipmentFilter}
            onChange={(e) => setEquipmentFilter(e.target.value)}
            options={[
              { value: "", label: "Todos" },
              ...(equipments.data ?? []).map((e) => ({ value: e.id, label: e.name })),
            ]}
          />
        </Field>
        <Field label="Severidade">
          <Select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            options={[{ value: "", label: "Todas" }, ...SEVERIDADE_OPCOES]}
          />
        </Field>
        <Field label="Tipo de evento">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={[
              { value: "",                    label: "Todos" },
              { value: "fired",               label: "Disparou" },
              { value: "recovered",           label: "Recuperou" },
              { value: "acknowledged",        label: "Reconhecido" },
              { value: "notification_sent",   label: "Notificado" },
              { value: "notification_failed", label: "Falha de notificação" },
            ]}
          />
        </Field>
        <div className="flex items-end">
          <Button
            variant="secondary"
            icon={<RefreshCcw className="h-4 w-4" />}
            onClick={() => events.refetch()}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <DataTable
        data={events.data}
        columns={columns}
        loading={events.isLoading}
        rowKey={(r) => r.id}
        emptyTitle="Nenhum evento"
        emptyDescription="Quando regras dispararem ou recuperarem, vão aparecer aqui."
      />
    </div>
  );
}

// ---------- helpers ---------------------------------------------------------

function ActiveAlertRow({
  alert, onAck, loading,
}: {
  alert: ActiveAlert;
  onAck: () => void;
  loading?: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="flex items-center gap-3">
        <Badge tone={SEVERITY_TONE[alert.severity]}>{alert.severity}</Badge>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {alert.equipment_name}
            </span>
            {alert.tenant_slug && (
              <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300">
                {alert.tenant_slug}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {alert.rule_kind} · {alert.message ?? "firing"} · {timeAgo(alert.since)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {alert.status === "acknowledged" ? (
          <Badge tone="default">reconhecido</Badge>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            icon={<CheckCheck className="h-4 w-4" />}
            loading={loading}
            onClick={onAck}
          >
            Reconhecer
          </Button>
        )}
      </div>
    </li>
  );
}

